import {hashChangeMap} from './HashChangeHandler';
import {nodeAddedMap} from './MutationHandler';
import {
  ensureShoelaceThemeLoaded,
  getShoelaceBaseUrl,
  handleContentPlaceholders,
  isContentVimeoPlayer,
  isContentYouTubePlayer,
} from './utils';

interface DialogElement extends HTMLElement {
  open: boolean;
  show(): void;
  hide(): void;
}

const tagName = 'sl-dialog';
export const DialogController = Object.freeze({
  tagName,
  selector: tagName,
  moduleUrl: `${getShoelaceBaseUrl()}components/dialog/dialog.js`,
  async ensureLoaded() {
    return Promise.all([
      ensureShoelaceThemeLoaded(),
      import(/* webpackIgnore: true */ DialogController.moduleUrl),
    ]);
  },
});

function isDialogElement(element: HTMLElement): element is DialogElement {
  return element.matches(DialogController.selector);
}

nodeAddedMap.set(DialogController.selector, (target) => {
  if (!isDialogElement(target)) {
    return;
  }

  if (!customElements.get(DialogController.tagName)) {
    DialogController.ensureLoaded();
  }

  // on close, trigger hash change if matching hash is target
  target.addEventListener('sl-hide', () => {
    if (!target.id) {
      return;
    }

    // remove the hash without a hashchange event
    if (window.location.hash === `#${target.id}`) {
      const url = new URL(window.location.href);
      url.hash = '';
      window.history.pushState({}, '', url.href);
    }
  });

  handleContentPlaceholders(target, (contentType, player) => {
    if (isContentVimeoPlayer(contentType, player)) {
      target.addEventListener('sl-show', () => {
        player.play();
      });
      target.addEventListener('sl-hide', () => {
        player.pause();
      });
    } else if (isContentYouTubePlayer(contentType, player)) {
      target.addEventListener('sl-show', () => {
        player.playVideo();
      });
      target.addEventListener('sl-hide', () => {
        player.pauseVideo();
      });
    }
  });
});

hashChangeMap.set(DialogController.selector, (event, target, isNew) => {
  if (!isDialogElement(target)) {
    return;
  }

  DialogController.ensureLoaded().then(() => {
    if (isNew) {
      if (!target.open) {
        target.show();
      }
    } else {
      if (target.open) {
        target.hide();
      }
    }
  });
});
